import React, { RefObject } from "react";

import { Button } from "@src/components/atoms/buttons/button/button";

import CloseIcon from "@src/assets/svg/close-icon.svg";

import styles from "./find-auto-match-blueprint-modal-content.module.scss";
import { FlexRowContainer } from "@src/components/templates/layouts/flex-row-container/flex-row-container";
import { renderBlueprintPopover } from "../../helpers/render-blueprint-popover";
import { handleCloseDescriptionPopover } from "../../helpers/handle-close-description-popover";
import { HorizontalSeparator } from "@src/components/atoms/horizontal-separator/horizontal-separator";
import { GlassyModalHeader } from "@src/components/molecules/glassy-modal-header/glassy-modal-header";
import { BlueprintTableWithSpinner } from "@src/components/organisms/blueprint-table-with-spinner/blueprint-table-with-spinner";
import { useFindAutoMatchBlueprintModalContentHook } from "./use-find-auto-match-blueprint-modal-content-hook";
import { BlueprintsArrayObjectTypes } from "@common/types/blueprint-types";
import { useI18N } from "@src/language/language-context";

const cssClassName = "find-auto-match-blueprint-modal-content";

interface PopoverPositionTypes {
  x: number;
  y: number;
}

interface UseFindAutoMatchBlueprintModalContentHookTypes {
  modalRef: RefObject<HTMLDivElement>;
  suggestedBlueprintsArray: BlueprintsArrayObjectTypes[];
  popoverPosition: PopoverPositionTypes;
  activeBlueprint: BlueprintsArrayObjectTypes;
  activePopover: BlueprintsArrayObjectTypes;
  loadingBlueprintsList: boolean;
  setPopoverPosition: (position: PopoverPositionTypes) => void;
  setActiveBlueprint: (blueprint: BlueprintsArrayObjectTypes) => void;
  setActivePopover: (popover: BlueprintsArrayObjectTypes) => void;
  closeModal: () => void;
  handleApplyBlueprint: () => void;
  blueprintSupportLinkBuilder: () => string;
}

interface FindAutoMatchBlueprintModalContentPropTypes {
  useHook?: () => UseFindAutoMatchBlueprintModalContentHookTypes;
}

export const FindAutoMatchBlueprintModalContent = ({
  useHook = useFindAutoMatchBlueprintModalContentHook,
}: FindAutoMatchBlueprintModalContentPropTypes) => {
  const {
    modalRef,
    suggestedBlueprintsArray,
    popoverPosition,
    activeBlueprint,
    activePopover,
    loadingBlueprintsList,
    setPopoverPosition,
    setActiveBlueprint,
    setActivePopover,
    closeModal,
    handleApplyBlueprint,
    blueprintSupportLinkBuilder,
  } = useHook();
  const i18n = useI18N();

  const texts = {
    subTitle: i18n("modules.findAutoMatchBlueprint.subTitle"),
    blueprint: i18n("modules.findAutoMatchBlueprint.blueprint"),
    blueprintLibrary: i18n("modules.findAutoMatchBlueprint.blueprintLibrary"),
    applyButton: i18n("modules.findAutoMatchBlueprint.applyButton"),
    cancelButton: i18n("modules.findAutoMatchBlueprint.cancelButton"),
    blueprintSupportText: i18n(
      "modules.findAutoMatchBlueprint.blueprintSupportText",
    ),
    blueprintSupportLink: i18n(
      "modules.findAutoMatchBlueprint.blueprintSupportLink",
    ),
  };

  return (
    <div ref={modalRef}>
      <GlassyModalHeader
        title={texts.subTitle}
        rightIcon={CloseIcon}
        rightIconFunction={closeModal}
      />
      <HorizontalSeparator verticalMargin={16} />
      <div>
        <div className={styles[`${cssClassName}__label`]}>
          {texts.blueprintLibrary}
        </div>
        <BlueprintTableWithSpinner
          ref={modalRef}
          loadingBlueprintsList={loadingBlueprintsList}
          suggestedBlueprintsArray={suggestedBlueprintsArray}
          activeBlueprint={activeBlueprint}
          setPopoverPosition={setPopoverPosition}
          setActiveBlueprint={setActiveBlueprint}
          setActivePopover={setActivePopover}
        />
      </div>
      <HorizontalSeparator verticalMargin={16} />
      <FlexRowContainer justifyContent="space-between">
        <FlexRowContainer>
          <Button
            fun={handleApplyBlueprint}
            text={texts.applyButton}
            variant={[!activeBlueprint ? "inactive" : ""]}
            disabled={!activeBlueprint}
          />
          <Button
            fun={closeModal}
            variant="secondary"
            text={texts.cancelButton}
          />
        </FlexRowContainer>
        <p className={styles[`${cssClassName}__text`]}>
          {texts.blueprintSupportText}{" "}
          <a
            className={styles[`${cssClassName}__link`]}
            rel="noreferrer"
            href={blueprintSupportLinkBuilder()}
          >
            {texts.blueprintSupportLink}
          </a>
        </p>
      </FlexRowContainer>
      {renderBlueprintPopover(activePopover, popoverPosition, (e) =>
        handleCloseDescriptionPopover(e, setActivePopover),
      )}
    </div>
  );
};
